import React, { useState, useEffect } from "react";
import { Table, Button, Dropdown } from "react-bootstrap";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PayInPdf from "../pages/PayInPdf";
import PayoutPdf from "../pages/PayoutPdf";
import PurchaseReceipt from "../pages/PurchaseReceipt";
import "./Table.css";
import { Buttons, Pagechange } from "./Buttons";
import { MdChevronRight, MdChevronLeft } from "react-icons/md";
import EstimationPdf from "../pages/EstimationPdf";
import API_DOMAIN from "../config/config";
import moment from "moment";
import companyId from "../config/companyId";

const Tables = (props) => {
  return (
    <Table responsive="md">
      <thead>
        <tr>
          {props.headers.map((data, index) => (
            <th key={index}>{data}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.body.length > 0 ? (
          props.body.map((rowData, rowIndex) => (
            <tr key={rowIndex}>
              {rowData.values.map((rowValue, colIndex) => (
                <td key={colIndex}>{rowValue}</td>
              ))}
            </tr>
          ))
        ) : (
          <tr className="text-center mx-auto">
            <td colSpan={props.headers.length}>No Record Found</td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

const ProductTable = (props) => {
  const navigate = useNavigate();
  const handlesalesreturnprintClick = (rowData) => {
    navigate("/console/sales/return", {
      state: { type: "pdfview", rowData: rowData },
    });
  };

  return (
    <Table responsive="md">
      <thead>
        <tr>
          {props.headers.map((data) => (
            <th key={data}>{data}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.body != null ? (
          props.body.map((rowData) => (
            <tr key={rowData.id || rowData.receipt_no}>
              {rowData.values.map((rowValue, index) => (
                <td key={index}>{rowValue}</td>
              ))}
              <td className="">
                <Dropdown>
                  <Dropdown.Toggle>
                    <Button className="action">
                      <BiDotsVerticalRounded />
                    </Button>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => handlesalesreturnprintClick(rowData)}
                    >
                      PDF
                    </Dropdown.Item>
                    <Dropdown.Item>View</Dropdown.Item>
                    <Dropdown.Item>Edit</Dropdown.Item>
                    <Dropdown.Item>Delete</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </td>
            </tr>
          ))
        ) : (
          <tr className="text-center mx-auto">
            <td colSpan={props.headers.length}>No Record Found</td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

const TableUI = ({
  headers,
  body,
  style,
  type,
  rowData,
  planViewAction,
  onClick,
  onDelete,
  handleEditClick,
  handleCheckboxChange,
  selectedEstimations,
  pageview,
}) => {
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const totalItems = body ? body.length : 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Slice the data to get the current page's items
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = body ? body.slice(startIndex, endIndex) : [];

  // Function to check if the next page has data
  const hasDataOnPage = (page) => {
    const pageStartIndex = (page - 1) * itemsPerPage;
    const pageEndIndex = pageStartIndex + itemsPerPage;
    const itemsOnPage = body ? body.slice(pageStartIndex, pageEndIndex) : [];
    return itemsOnPage.length > 0;
  };

  // Handle Next Page
  const nextPage = () => {
    const nextPageNumber = currentPage + 1;
    if (nextPageNumber <= totalPages && hasDataOnPage(nextPageNumber)) {
      setCurrentPage(nextPageNumber);
    }
  };

  // Handle Previous Page
  const prevPage = () => {
    const prevPageNumber = currentPage - 1;
    if (prevPageNumber >= 1 && hasDataOnPage(prevPageNumber)) {
      setCurrentPage(prevPageNumber);
    }
  };

  // Ensure currentPage stays within valid bounds
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    } else if (currentPage < 1 && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, body]);

  const navigate = useNavigate();

  const handleUserEdit = (rowData) => {
    handleEditClick(rowData);
  };

  const handleUserDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(`${API_DOMAIN}/user.php?id=${companyId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          delete_user_id: id,
        }),
      });

      const responseData = await response.json();
      if (responseData.head.code === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleCompanyEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handlePartyPurchaseEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handlePartyPurchaseDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(`${API_DOMAIN}/purchase_party.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          delete_party_id: id,
          company_id: companyId,
        }),
      });

      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handlePartyPurchaseTransactionEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handlePartySalesEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handlePartySalesDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(`${API_DOMAIN}/sales_party.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          delete_party_id: id,
          company_id: companyId,
        }),
      });

      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handlePartySalesTransactionEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleItemProdutEditClick = (rowData) => {
    navigate("/console/item/create", {
      state: { type: "edit", rowData: rowData },
    });
  };

  const handleItemProductDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(
        `${API_DOMAIN}/products.php?id=${companyId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            product_id: id,
          }),
        }
      );
      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleItemProdutTransactionEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleItemCategoryEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleItemCategoryDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(
        `${API_DOMAIN}/category.php?id=${companyId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            category_id: id,
          }),
        }
      );

      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleItemCategoryTransactionEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleItemUnitEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleItemUnitDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(`${API_DOMAIN}/unit.php?id=${companyId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          unit_id: id,
        }),
      });

      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleItemUnitTransactionEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handlePurchaseBillEditClick = (rowData) => {
    navigate("/console/purchase/bill/create", {
      state: { type: "edit", rowData: rowData },
    });
  };

  const handlePurchaseBillDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(
        `${API_DOMAIN}/purchase_bill.php?id=${companyId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            purchase_id: id,
          }),
        }
      );

      const responseData = await response.json();
      console.log(responseData);
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handlePurchasePayoutEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handlePurchasePayoutDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(`${API_DOMAIN}/payout.php?id=${companyId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          payout_id: id,
        }),
      });

      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handlePurchaseReturnEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleSalesInvoiceEditClick = (rowData) => {
    navigate("/console/sales/invoice/create", {
      state: { type: "edit", rowData: rowData },
    });
  };

  const handleSalesInvoiceDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(
        `${API_DOMAIN}/salesinvoice.php?id=${companyId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            invoice_id: id,
          }),
        }
      );

      const responseData = await response.json();
      console.log(responseData);
      if (responseData.status === 200) {
        window.location.reload();
      } else {
        console.log(responseData.msg);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSalesInvoicePrintClick = (rowData) => {
    handleEditClick(rowData);
    navigate("/console/sales/invoice", {
      state: { type: "print", rowData: rowData },
    });
  };

  const handleSalesEstimatePrintClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleSalesEstimateEditClick = (rowData) => {
    navigate("/console/sales/estimation/create", {
      state: { type: "edit", rowData: rowData },
    });
  };

  const handleSalesEstimateDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(
        `${API_DOMAIN}/sales_estimation.php?id=${companyId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            estimate_id: id,
          }),
        }
      );

      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handlewholesaleInvoicePrintClick = (rowData) => {
    handleEditClick(rowData);
    navigate("/console/sales/wholesale", {
      state: { type: "print", rowData: rowData },
    });
  };
  const handlewholesaleInvoiceEditClick = (rowData) => {
    handleEditClick(rowData);
    navigate("/console/wholesale/create", {
      state: { type: "edit", rowData: rowData },
    });
  };

  const handlewholeSalesInvoiceDeleteClick = async (id, onDelete) => {
    try {
      console.log(id);

      const response = await fetch(`${API_DOMAIN}/wholesale_invoice.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          delete_wholesale_id: id,
        }),
      });

      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
      } else {
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };
  const handleSalesPayinEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleSalesPayinDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(`${API_DOMAIN}/payin.php?id=${companyId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          payin_id: id,
        }),
      });

      const responseData = await response.json();
      console.log("responseData", responseData);
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSalesReturnEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleExpancesCategoryEdit = (rowData) => {
    handleEditClick(rowData);
  };

  const handleExpancesCategoryTransactionEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleExpancesCategoryDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(
        `${API_DOMAIN}/expensecategory.php?id=${companyId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            delete_category_id: id,
          }),
        }
      );

      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
        window.location.reload();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleExpancesItemEdit = (rowData) => {
    handleEditClick(rowData);
  };

  const handleItemTransactionEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleExpancesItemDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(
        `${API_DOMAIN}/expenseitem.php?id=${companyId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            expenses_item_id: id,
          }),
        }
      );

      const responseData = await response.json();
      if (responseData.status === 200) {
        onDelete();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handlePurvhasePayoutEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleCompanEditClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleCompanyViewClick = (rowData) => {
    handleEditClick(rowData);
  };

  const handleCompanyDeleteClick = async (id) => {
    try {
      const response = await fetch(
        "https://client.barbikan.in/orchids/api/src/plan.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            delete_plan_id: id,
          }),
        }
      );

      const responseData = await response.json();
      if (responseData.head.code === 200) {
        navigate("/console/plan");
        window.location.reload();
      } else {
        console.log(responseData.head.msg);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleRowClick = (rowData) => {
    onClick(rowData);
  };
  const handleSalesEstimationEditClick = (rowData) => {
    navigate("/console/sales/estimation/create", {
      state: { type: "edit", rowData: rowData },
    });
  };

  const handleSalesEstimationDeleteClick = async (id, onDelete) => {
    try {
      const response = await fetch(
        `${API_DOMAIN}/sales_estimation.php?id=${companyId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sales_estimation_id: id,
          }),
        }
      );

      const responseData = await response.json();
      console.log(responseData);
      if (responseData.status === 200) {
        window.location.reload();
      } else {
        console.log(responseData.msg);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSalesEstimationPrintClick = (rowData) => {
    handleEditClick(rowData);
    navigate("/console/sales/estimation", {
      state: { type: "print", rowData: rowData },
    });
  };

  const getSerialNumber = (rowIndex) => (currentPage - 1) * itemsPerPage + (rowIndex + 1);

  return (
    <>
      {pageview === "yes" && pageview !== undefined ? (
        <div className="text-end">
          <span className="mx-1">
            Page {currentPage} of {totalPages}
          </span>
          <span className="mx-1">
            <Pagechange
              label={<MdChevronLeft />}
              onClick={prevPage}
              disabled={currentPage === 1 || totalItems === 0}
            />
          </span>
          <span className="mx-1">
            <Pagechange
              label={<MdChevronRight />}
              onClick={nextPage}
              disabled={currentPage === totalPages || totalItems === 0}
            />
          </span>
        </div>
      ) : null}

      <Table responsive="md" style={style}>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {currentItems.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="text-center py-3">
                No record found
              </td>
            </tr>
          ) : (
            currentItems.map((rowData, rowIndex) => (
              <tr key={rowIndex}>
                {type === "USER" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.name}</td>
                    <td>{rowData.phone_number}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleUserEdit(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleUserDeleteClick(rowData.user_id, onDelete)
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "company" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.company_name}</td>
                    <td>{rowData.gst_no}</td>
                    <td>{rowData.mobile_number}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleCompanyEditClick(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "partypurches" && (
                  <>
                    <td onClick={() => handleRowClick(rowData)}>
                      {rowData.party_name}
                    </td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              handlePartyPurchaseEditClick(rowData)
                            }
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handlePartyPurchaseDeleteClick(
                                rowData.party_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "partypurchestransactions" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.type}</td>
                    <td>{rowData.date}</td>
                    <td>{rowData.receipt_no}</td>
                    <td>{rowData.amount}</td>
                    <td>{rowData.paid}</td>
                    <td>{rowData.balance}</td>
                  </>
                )}
                {type === "partysales" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.party_name}</td>
                    <td>{rowData.mobile_number}</td>
                    <td>{rowData.billing_address}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        {/* <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handlePartySalesEditClick(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handlePartySalesDeleteClick(
                                rowData.party_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu> */}
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "partysalestransactions" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.type}</td>
                    <td>{rowData.date}</td>
                    <td>{rowData.receipt_no}</td>
                    <td>{rowData.amount}</td>
                    <td>{rowData.paid}</td>
                    <td>{rowData.balance}</td>
                  </>
                )}
                {type === "itemsproduct" && (
                  <>
                    <td onClick={() => handleRowClick(rowData)}>
                      {rowData.product_name}
                    </td>
                    <td onClick={() => handleRowClick(rowData)}>
                      {rowData.unit_rate}
                    </td>
                    {/* <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleItemProdutEditClick(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleItemProductDeleteClick(
                                rowData.product_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td> */}
                  </>
                )}
                {type === "itemsproducttransactions" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.type}</td>
                    <td>{rowData.date}</td>
                    <td>{rowData.receipt_no}</td>
                    <td>{rowData.amount}</td>
                  </>
                )}
                {type === "itemscategory" && (
                  <>
                    <td onClick={() => handleRowClick(rowData)}>
                      {rowData.category_name}
                    </td>
                    <td onClick={() => handleRowClick(rowData)}>
                      {rowData.product_count}
                    </td>
                    {/* <td className="">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleItemCategoryEditClick(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleItemCategoryDeleteClick(
                                rowData.category_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td> */}
                  </>
                )}
                {type === "itemscategorytransactions" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.product_name}</td>
                    <td className="text-end">{rowData.current_stock}</td>
                  </>
                )}
                {type === "itemsunit" && (
                  <>
                    <td onClick={() => handleRowClick(rowData)}>
                      {rowData.unit_name}
                    </td>
                    <td className="">
                      <Dropdown className="text-end">
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleItemUnitEditClick(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleItemUnitDeleteClick(
                                rowData.unit_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "itemsunitransactions" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>
                      1 {rowData.unit_name} = {rowData.rate}{" "}
                      {rowData.subunit_name}
                    </td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              handleItemUnitTransactionEditClick(rowData)
                            }
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleCompanyDeleteClick(rowData.id)}
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "purchasebill" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.bill_date}</td>
                    <td>{rowData.bill_no}</td>
                    <td>{rowData.party_details.party_name}</td>
                    <td>{rowData.total}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <PDFDownloadLink
                            document={<PurchaseReceipt data={rowData} />}
                            fileName="purchasebill.pdf"
                          >
                            {({ blob, url, loading, error }) => (
                              <a
                                className="dropdown-item"
                                role="button"
                                tabIndex="0"
                                href={url}
                              >
                                View Pdf
                              </a>
                            )}
                          </PDFDownloadLink>
                          <Dropdown.Item
                            onClick={() => handlePurchaseBillEditClick(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handlePurchaseBillDeleteClick(
                                rowData.purchase_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "salesestimation" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    {/* <td>
                      <input
                        type="checkbox"
                        checked={selectedEstimations.includes(
                          rowData.sales_estimation_id
                        )}
                        onChange={() =>
                          handleCheckboxChange(rowData.sales_estimation_id)
                        }
                      />
                    </td> */}
                    <td>{formatDate(rowData.bill_date)}</td>
                    <td>{rowData.bill_no}</td>
                    <td>{rowData.party_name}</td>
                    <td>{rowData.total}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              handleSalesEstimationPrintClick(rowData)
                            }
                          >
                            Print
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleSalesEstimationEditClick(rowData)
                            }
                          >
                            Edit
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={() =>
                              handleSalesEstimationDeleteClick(
                                rowData.sales_estimation_id
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "purchasepayout" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{moment(rowData.voucher_date).format("DD-MM-YYYY")}</td>
                    <td>{rowData.voucher_no}</td>
                    <td>{rowData.party_details.party_name}</td>
                    <td>{rowData.paid}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <PDFDownloadLink
                            document={<PayoutPdf data={rowData} />}
                            fileName="payout.pdf"
                          >
                            {({ blob, url, loading, error }) => (
                              <a
                                className="dropdown-item"
                                role="button"
                                tabIndex="0"
                                href={url}
                              >
                                View Pdf
                              </a>
                            )}
                          </PDFDownloadLink>
                          <Dropdown.Item
                            onClick={() =>
                              handlePurchasePayoutEditClick(rowData)
                            }
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handlePurchasePayoutDeleteClick(
                                rowData.payout_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "purchasereturn" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.date}</td>
                    <td>{rowData.receipt_no}</td>
                    <td>{rowData.name}</td>
                    <td>{rowData.total}</td>
                    <td>{rowData.received}</td>
                    <td>{rowData.due}</td>
                    <td className="">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              handlePurchaseReturnEditClick(rowData)
                            }
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleCompanyDeleteClick(rowData.id)}
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "salesinvoice" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{moment(rowData.bill_date).format("DD-MM-YYYY")}</td>
                    <td>{rowData.bill_no}</td>
                    <td>{rowData.party_name}</td>
                    <td>{rowData.total}</td>
                    {/* <td>{rowData.paid}</td> */}
                    {/* <td>{rowData.balance}</td> */}
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              handleSalesInvoicePrintClick(rowData)
                            }
                          >
                            Print
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleSalesInvoiceEditClick(rowData)}
                          >
                            Edit
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={() =>
                              handleSalesInvoiceDeleteClick(rowData.invoice_id)
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "wholesaleinvoice" && (
                  <>
                    {" "}
                    {/* Fragment shorthand */}
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{moment(rowData.bill_date).format("DD-MM-YYYY")}</td>
                    <td>{rowData.bill_no}</td>
                    <td>{rowData.partyname}</td>
                    <td>{rowData.total}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              handlewholesaleInvoicePrintClick(rowData)
                            }
                          >
                            Print
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handlewholesaleInvoiceEditClick(rowData)
                            }
                          >
                            Edit
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={() =>
                              handlewholeSalesInvoiceDeleteClick(
                                rowData.invoice_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "salesestimate" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.estimate_date}</td>
                    <td>{rowData.estimate_no}</td>
                    <td>{rowData.party_details.party_name}</td>
                    <td>{rowData.total}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <PDFDownloadLink
                            document={<EstimationPdf data={rowData} />}
                            fileName="SalesEstimate.pdf"
                          >
                            {({ blob, url, loading, error }) => (
                              <a
                                className="dropdown-item"
                                role="button"
                                tabIndex="0"
                                href={url}
                              >
                                View Pdf
                              </a>
                            )}
                          </PDFDownloadLink>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "salespayin" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.receipt_date}</td>
                    <td>{rowData.receipt_no}</td>
                    <td>{rowData.party_details.party_name}</td>
                    <td>{rowData.paid}</td>
                    <td className="">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <PDFDownloadLink
                            document={<PayInPdf data={rowData} />}
                            fileName="payin.pdf"
                          >
                            {({ blob, url, loading, error }) => (
                              <a
                                className="dropdown-item"
                                role="button"
                                tabIndex="0"
                                href={url}
                              >
                                View Pdf
                              </a>
                            )}
                          </PDFDownloadLink>
                          <Dropdown.Item
                            onClick={() => handleSalesPayinEditClick(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleSalesPayinDeleteClick(
                                rowData.payin_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "salesreturn" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.date}</td>
                    <td>{rowData.receipt_no}</td>
                    <td>{rowData.name}</td>
                    <td>{rowData.total}</td>
                    <td>{rowData.paid}</td>
                    <td>{rowData.due}</td>
                    <td className="">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleSalesReturnEditClick(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleCompanyDeleteClick(rowData.id)}
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "expensescategory" && (
                  <>
                    <td onClick={() => handleRowClick(rowData)}>
                      {rowData.category_name}
                    </td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleExpancesCategoryEdit(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleExpancesCategoryDeleteClick(
                                rowData.category_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "expensescategorytransactions" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.date}</td>
                    <td>{rowData.receipt_no}</td>
                    <td>{rowData.party_name}</td>
                    <td>{rowData.amount}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              handleExpancesCategoryTransactionEditClick(
                                rowData
                              )
                            }
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleCompanyDeleteClick(rowData.id)}
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "expensesitems" && (
                  <>
                    <td onClick={() => handleRowClick(rowData)}>
                      {rowData.party_name}
                    </td>
                    <td onClick={() => handleRowClick(rowData)}>
                      {rowData.total}
                    </td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => handleExpancesItemEdit(rowData)}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              handleExpancesItemDeleteClick(
                                rowData.expenses_item_id,
                                onDelete
                              )
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
                {type === "expensesitemtransactions" && (
                  <>
                    <td>{getSerialNumber(rowIndex)}</td>
                    <td>{rowData.date}</td>
                    <td>{rowData.receipt_no}</td>
                    <td>{rowData.party_name}</td>
                    <td>{rowData.amount}</td>
                    <td className="text-end">
                      <Dropdown>
                        <Dropdown.Toggle>
                          <Button className="action">
                            <BiDotsVerticalRounded />
                          </Button>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() =>
                              handleItemTransactionEditClick(rowData)
                            }
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => handleCompanyDeleteClick(rowData.id)}
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {pageview === "yes" && pageview !== undefined ? (
        <div className="text-end">
          <span className="mx-1">
            Page {currentPage} of {totalPages}
          </span>
          <span className="mx-1">
            <Pagechange
              label={<MdChevronLeft />}
              onClick={prevPage}
              disabled={currentPage === 1 || totalItems === 0}
            />
          </span>
          <span className="mx-1">
            <Pagechange
              label={<MdChevronRight />}
              onClick={nextPage}
              disabled={currentPage === totalPages || totalItems === 0}
            />
          </span>
        </div>
      ) : null}
    </>
  );
};
const formatDate = (dateString, format = "DD-MM-YY") => {
  if (!dateString) return "N/A"; // Handle empty or invalid dates
  const date = moment(dateString, ["YYYY-MM-DD", "DD/MM/YYYY"], true); // Parse multiple possible formats
  return date.isValid() ? date.format(format) : "Invalid Date";
};
export { Tables, ProductTable, TableUI, formatDate };
